import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaInjector from '@/components/base/SchemaInjector';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { websiteSchema, getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/mocks/schema';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  useScrollToTop();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您要查看的内容不存在或已下架</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const webPageSchema = getWebPageSchema(
    `https://www.chuanmeizaixian.com/article/${article.id}`,
    `${article.title} - 传媒在线观看`,
    article.excerpt,
    article.date,
  );
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: 'https://www.chuanmeizaixian.com/' },
    { name: article.categoryName, url: `https://www.chuanmeizaixian.com/category/${article.category}` },
    { name: article.title, url: `https://www.chuanmeizaixian.com/article/${article.id}` },
  ]);
  const articleSchema = getArticleSchema(
    article.title,
    article.excerpt,
    `https://www.chuanmeizaixian.com/article/${article.id}`,
    article.date,
    article.author,
    article.image,
  );

  return (
    <>
      <SchemaInjector schema={[websiteSchema, webPageSchema, breadcrumbSchema, articleSchema]} />

      <main>
        <Navbar />

        <div className="pt-20 bg-background-50">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: article.categoryName, url: `/category/${article.category}` },
              { name: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full font-medium whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 传媒在线观看`}
                className="w-full object-cover object-top max-h-[500px]"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-12
                prose-headings:font-heading prose-headings:text-foreground-900
                prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200 pt-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-background-200 transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-8 py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <div className="flex items-center text-xs text-foreground-400">
                        <span>{ra.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}